import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import './SupplierManageModal.css';
import { getPlanConfig, shouldShowVerifiedBadge } from '../../utils/membershipUtils';

const SupplierManageModal = ({ isOpen, onClose, supplier, loading, onViewProducts }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const { user } = useAuth();
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  
  // Show mobile state
  const [showMobile, setShowMobile] = useState(false);
  
  // Show certificates state
  const [showCertificates, setShowCertificates] = useState(false);

  // Handle form input change
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle Inquire Now button
  const handleInquireNow = () => {
    setActiveTab('contact');
  };

  // Handle View Mobile button
  const handleViewMobile = () => {
    setShowMobile(!showMobile);
  };

  // Handle Chat Now button
  const handleChatNow = () => {
    alert('Chat feature coming soon!');
  };

  // Handle See More certificates
  const handleSeeMoreCertificates = () => {
    setShowCertificates(!showCertificates);
  };

  // Handle form submit
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is trying to send inquiry to their own company
    if (isOwnCompany) {
      alert('You cannot send an inquiry to your own company');
      return;
    }
    
    // Validate required fields
    if (!contactForm.name || !contactForm.email || !contactForm.subject || !contactForm.message) {
      alert('Please fill in all required fields');
      return;
    }
    
    setFormSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log('Inquiry submitted:', contactForm);
      setFormSuccess(true);
      setFormSubmitting(false);
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setContactForm({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
        setFormSuccess(false);
      }, 2000);
    }, 1000);
  };

  // Handle cancel
  const handleFormCancel = () => {
    setContactForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: '',
      message: ''
    });
    setActiveTab('profile');
  };

  if (!isOpen) return null;

  // Map real supplier data to modal format
  const mapSupplierData = (supplierData) => {
    if (!supplierData) return null;
    
    // Parse JSON fields if they're strings
    const parseJSON = (field) => {
      if (!field) return [];
      if (Array.isArray(field)) return field;
      try {
        return JSON.parse(field);
      } catch {
        return [];
      }
    };

    return {
      id: supplierData.id,
      name: supplierData.name || 'Unknown Company',
      tagline: `Trusted ${supplierData.businessType || 'Supplier'} with ${supplierData.memberYears || 1}+ Years of Industry Experience`,
      description: supplierData.description || supplierData.companyOverview || 'No description available.',
      logo: supplierData.logo || 'https://api.builder.io/api/v1/image/assets/TEMP/7a0d53179ca4074c1523b761c4cd8b44535f4107?width=160',
      coverImage: supplierData.coverImage || 'https://api.builder.io/api/v1/image/assets/TEMP/f9b3e358749e8058cad012bb549d2006895ca9b0?width=3044',
      membershipType: supplierData.isGlobal ? 'Global' : (supplierData.membershipPlan || 'Basic'),
      isVerified: supplierData.isVerified || supplierData.verificationLevel === 'verified',
      country: supplierData.country || 'UAE',
      countryFlag: supplierData.countryFlag || 'https://api.builder.io/api/v1/image/assets/TEMP/78d52fdc38226f3a74e247f4064857fd7be60a1c?width=124',
      yearsActive: `${supplierData.memberYears || 1} Year`,
      businessType: supplierData.businessType || 'Manufacturer',
      reviewCount: supplierData.reviews || 0,
      productCount: supplierData.products?.length || supplierData._count?.products || 0,
      certificatesDescription: supplierData.description || 'Quality certified supplier providing premium products.',
      contactInfo: {
        description: 'Have questions or ready to get started? Our team is here to help with quotes, product info, or partnership opportunities. Reach out today — let\'s build something great together.',
        telephone: supplierData.phoneNumber || supplierData.phone || '',
        email: supplierData.email || '',
        address: supplierData.address || '',
        website: supplierData.websiteLink || '',
      },
      basicInfo: {
        establishYear: supplierData.establishYear || 'N/A',
        licenseNumber: supplierData.licenseNumber || 'N/A',
        serviceArea: supplierData.serviceArea || supplierData.country || 'N/A',
        numberOfEmployees: supplierData.numberOfEmployees || 'N/A',
        category: supplierData.category || 'Construction',
        annualSale: supplierData.annualSales || 'N/A',
        totalCapitalization: supplierData.totalCapitalization || 'N/A',
        websiteLink: supplierData.websiteLink || 'N/A',
      },
      companyOverview: supplierData.companyOverview || supplierData.description || 'No company overview available.',
      companyNotes: supplierData.companyNotes || 'No additional notes.',
      additionalNotes: parseJSON(supplierData.companyNotes) || [],
      products: (supplierData.products || []).map((product, index) => ({
        id: product.id || index + 1,
        title: product.name || product.title || 'Product',
        price: `$${product.priceMin || 0} - $${product.priceMax || 0}`,
        unit: product.unit || 'Tons',
        moq: product.moq || '1',
        membershipType: supplierData.isGlobal ? 'Global' : 'Basic',
        isVerified: supplierData.isVerified,
        country: supplierData.country || 'UAE',
        countryFlag: supplierData.countryFlag || 'https://api.builder.io/api/v1/image/assets/TEMP/16de30aa05de5bb0528325e87825f6bd498032ea?width=62',
        yearsActive: `${supplierData.memberYears || 1} Year`,
        businessType: supplierData.businessType || 'Manufacturer',
        sellerLogo: supplierData.logo || 'https://api.builder.io/api/v1/image/assets/TEMP/02212f336c525c8455fe3e939aee68a887c005ec?width=332',
        image: product.image || 'https://api.builder.io/api/v1/image/assets/TEMP/aa43c94414ed9787153d2f716ee1a7a83efbd99f?width=226',
      })),
      companySnapshots: parseJSON(supplierData.snapshots) || [
        'https://api.builder.io/api/v1/image/assets/TEMP/118cef06ed13db06d21f700b3d51b9e240895c5a?width=470',
        'https://api.builder.io/api/v1/image/assets/TEMP/118cef06ed13db06d21f700b3d51b9e240895c5a?width=470',
        'https://api.builder.io/api/v1/image/assets/TEMP/118cef06ed13db06d21f700b3d51b9e240895c5a?width=470',
      ],
      brochures: parseJSON(supplierData.brochures) || [],
    };
  };

  // Default supplier data (fallback)
  const defaultSupplier = {
    name: 'Eiffage Construction',
    tagline: 'Trusted Cement Supplier with 25+ Years of Industry Experience',
    description: 'Al Noor Cement Ltd. is a leading manufacturer and distributor of high-quality cement products across Bangladesh. We proudly serve large infrastructure projects, residential contractors, and wholesale buyers with consistent quality and timely delivery.',
    logo: 'https://api.builder.io/api/v1/image/assets/TEMP/7a0d53179ca4074c1523b761c4cd8b44535f4107?width=160',
    coverImage: 'https://api.builder.io/api/v1/image/assets/TEMP/f9b3e358749e8058cad012bb549d2006895ca9b0?width=3044',
    membershipType: 'Global',
    isVerified: true,
    country: 'United State',
    countryFlag: 'https://api.builder.io/api/v1/image/assets/TEMP/78d52fdc38226f3a74e247f4064857fd7be60a1c?width=124',
    yearsActive: '01 Year',
    businessType: 'Manufacture',
    reviewCount: 0,
    productCount: 20,
    certificatesDescription: 'Al Noor Cement Ltd. is a leading manufacturer and distributor of high-quality cement',
    contactInfo: {
      description: 'Have questions or ready to get started? Our team is here to help with quotes, product info, or partnership opportunities. Reach out today — let\'s build something great together.',
    },
    basicInfo: {
      establishYear: '2012',
      licenseNumber: 'Hd234235',
      serviceArea: 'Dubai, Saudi Arab, Qatar',
      numberOfEmployees: '234',
      category: 'Construction',
      annualSale: '$3.2M USD',
      totalCapitalization: '$12M',
      websiteLink: 'www.tradeindia.com/join_now/verification.html',
    },
    companyOverview: 'Established in 2014, this family-run Italian restaurant is located in a high-footfall downtown area with consistent loyal customer traffic. Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor. Established in 2014, this family-run Italian restaurant is located in a high-footfall downtown area with consistent loyal customer traffic. Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor. Established in 2014, this family-run Italian restaurant is located in a high-footfall downtown area with consistent loyal customer traffic. Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor.',
    companyNotes: 'Established in 2014, this family-run Italian restaurant is located in a high-footfall downtown area with consistent loyal customer traffic. Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor. Established in 2014, this family-run Italian restaurant is located in a high-footfall downtown area with consistent loyal customer traffic. Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor.',
    additionalNotes: [
      'Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor.',
      'Fully furnished, with a turnkey setup, trained staff, and high online ratings. Great opportunity for a hands-on operator or investor.',
    ],
    products: [
      {
        id: 1,
        title: 'Construction of 15 story commercial office building with modern amenities and parking facilities.',
        price: '$2.5 - $3.2 USD',
        unit: 'Tons',
        moq: '15 Tons',
        membershipType: 'Global',
        isVerified: true,
        country: 'USA',
        countryFlag: 'https://api.builder.io/api/v1/image/assets/TEMP/16de30aa05de5bb0528325e87825f6bd498032ea?width=62',
        yearsActive: '01 Year',
        businessType: 'Manufacture',
        sellerLogo: 'https://api.builder.io/api/v1/image/assets/TEMP/02212f336c525c8455fe3e939aee68a887c005ec?width=332',
        image: 'https://api.builder.io/api/v1/image/assets/TEMP/aa43c94414ed9787153d2f716ee1a7a83efbd99f?width=226',
      },
      {
        id: 2,
        title: 'Construction of 15 story commercial office building with modern amenities and parking facilities.',
        price: '$2.5 - $3.2 USD',
        unit: 'Tons',
        moq: '15 Tons',
        membershipType: 'Global',
        isVerified: true,
        country: 'USA',
        countryFlag: 'https://api.builder.io/api/v1/image/assets/TEMP/16de30aa05de5bb0528325e87825f6bd498032ea?width=62',
        yearsActive: '01 Year',
        businessType: 'Manufacture',
        sellerLogo: 'https://api.builder.io/api/v1/image/assets/TEMP/02212f336c525c8455fe3e939aee68a887c005ec?width=332',
        image: 'https://api.builder.io/api/v1/image/assets/TEMP/aa43c94414ed9787153d2f716ee1a7a83efbd99f?width=226',
      },
      {
        id: 3,
        title: 'Construction of 15 story commercial office building with modern amenities and parking facilities.',
        price: '$2.5 - $3.2 USD',
        unit: 'Tons',
        moq: '15 Tons',
        membershipType: 'Global',
        isVerified: true,
        country: 'USA',
        countryFlag: 'https://api.builder.io/api/v1/image/assets/TEMP/16de30aa05de5bb0528325e87825f6bd498032ea?width=62',
        yearsActive: '01 Year',
        businessType: 'Manufacture',
        sellerLogo: 'https://api.builder.io/api/v1/image/assets/TEMP/02212f336c525c8455fe3e939aee68a887c005ec?width=332',
        image: 'https://api.builder.io/api/v1/image/assets/TEMP/aa43c94414ed9787153d2f716ee1a7a83efbd99f?width=226',
      },
      {
        id: 4,
        title: 'Construction of 15 story commercial office building with modern amenities and parking facilities.',
        price: '$2.5 - $3.2 USD',
        unit: 'Tons',
        moq: '15 Tons',
        membershipType: 'Global',
        isVerified: true,
        country: 'USA',
        countryFlag: 'https://api.builder.io/api/v1/image/assets/TEMP/16de30aa05de5bb0528325e87825f6bd498032ea?width=62',
        yearsActive: '01 Year',
        businessType: 'Manufacture',
        sellerLogo: 'https://api.builder.io/api/v1/image/assets/TEMP/02212f336c525c8455fe3e939aee68a887c005ec?width=332',
        image: 'https://api.builder.io/api/v1/image/assets/TEMP/aa43c94414ed9787153d2f716ee1a7a83efbd99f?width=226',
      },
      {
        id: 5,
        title: 'Construction of 15 story commercial office building with modern amenities and parking facilities.',
        price: '$2.5 - $3.2 USD',
        unit: 'Tons',
        moq: '15 Tons',
        membershipType: 'Global',
        isVerified: true,
        country: 'USA',
        countryFlag: 'https://api.builder.io/api/v1/image/assets/TEMP/16de30aa05de5bb0528325e87825f6bd498032ea?width=62',
        yearsActive: '01 Year',
        businessType: 'Manufacture',
        sellerLogo: 'https://api.builder.io/api/v1/image/assets/TEMP/02212f336c525c8455fe3e939aee68a887c005ec?width=332',
        image: 'https://api.builder.io/api/v1/image/assets/TEMP/aa43c94414ed9787153d2f716ee1a7a83efbd99f?width=226',
      },
    ],
    companySnapshots: [
      'https://api.builder.io/api/v1/image/assets/TEMP/118cef06ed13db06d21f700b3d51b9e240895c5a?width=470',
      'https://api.builder.io/api/v1/image/assets/TEMP/118cef06ed13db06d21f700b3d51b9e240895c5a?width=470',
      'https://api.builder.io/api/v1/image/assets/TEMP/118cef06ed13db06d21f700b3d51b9e240895c5a?width=470',
      'https://api.builder.io/api/v1/image/assets/TEMP/118cef06ed13db06d21f700b3d51b9e240895c5a?width=470',
      'https://api.builder.io/api/v1/image/assets/TEMP/118cef06ed13db06d21f700b3d51b9e240895c5a?width=470',
      'https://api.builder.io/api/v1/image/assets/TEMP/118cef06ed13db06d21f700b3d51b9e240895c5a?width=470',
    ],
  };

  // Use mapped supplier data or fallback to default
  const data = supplier ? mapSupplierData(supplier) : defaultSupplier;
  
  // Check if this is user's own company
  const isOwnCompany = user && user.sellerId && (
    user.sellerId === supplier?.id || 
    user.sellerId === supplier?.userId ||
    user.sellerId === data?.id
  );

  const VerifiedBadge = ({ size = 'small' }) => (
    <svg 
      className={`verified-badge ${size}`} 
      width={size === 'small' ? '78' : '113'} 
      height={size === 'small' ? '21' : '30'} 
      viewBox={size === 'small' ? '0 0 88 22' : '0 0 113 30'} 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={size === 'small' 
        ? "M22 11C22 11.9323 20.7329 12.6871 20.5034 13.5472C20.2653 14.4377 20.9782 15.7221 20.528 16.5015C20.0705 17.2923 18.597 17.314 17.9562 17.9562C17.314 18.5984 17.2923 20.0705 16.5014 20.528C15.7221 20.9782 14.4377 20.2653 13.5472 20.5034C12.6871 20.7329 11.9337 22 11 22C10.0663 22 9.31291 20.7329 8.45276 20.5034C7.56231 20.2653 6.27788 20.9782 5.49856 20.528C4.70769 20.0705 4.68605 18.597 4.04382 17.9562C3.4016 17.3154 1.92954 17.2923 1.47205 16.5015C1.02177 15.7221 1.73472 14.4377 1.49659 13.5472C1.26712 12.6871 0 11.9338 0 11C0 10.0663 1.26712 9.31292 1.49659 8.45278C1.73472 7.56233 1.02177 6.27789 1.47205 5.49857C1.92954 4.7077 3.40305 4.68605 4.04382 4.04383C4.68605 3.40161 4.70769 1.92955 5.49856 1.47206C6.27788 1.02178 7.56231 1.73472 8.45276 1.49659C9.31291 1.26712 10.0663 0 11 0C11.9337 0 12.6871 1.26712 13.5472 1.49659C14.4377 1.73472 15.7221 1.02178 16.5014 1.47206C17.2923 1.92955 17.314 3.40305 17.9562 4.04383C18.5984 4.68461 20.0705 4.7077 20.528 5.49857C20.9782 6.27789 20.2653 7.56233 20.5034 8.45278C20.7329 9.31292 22 10.0663 22 11Z"
        : "M22 15C22 15.9323 20.7329 16.6871 20.5034 17.5472C20.2653 18.4377 20.9782 19.7221 20.528 20.5015C20.0705 21.2923 18.597 21.314 17.9562 21.9562C17.314 22.5984 17.2923 24.0705 16.5014 24.528C15.7221 24.9782 14.4377 24.2653 13.5472 24.5034C12.6871 24.7329 11.9337 26 11 26C10.0663 26 9.31291 24.7329 8.45276 24.5034C7.56231 24.2653 6.27788 24.9782 5.49856 24.528C4.70769 24.0705 4.68605 22.597 4.04382 21.9562C3.4016 21.3154 1.92954 21.2923 1.47205 20.5015C1.02177 19.7221 1.73472 18.4377 1.49659 17.5472C1.26712 16.6871 0 15.9338 0 15C0 14.0663 1.26712 13.3129 1.49659 12.4528C1.73472 11.5623 1.02177 10.2779 1.47205 9.49857C1.92954 8.7077 3.40305 8.68605 4.04382 8.04383C4.68605 7.40161 4.70769 5.92955 5.49856 5.47206C6.27788 5.02178 7.56231 5.73472 8.45276 5.49659C9.31291 5.26712 10.0663 4 11 4C11.9337 4 12.6871 5.26712 13.5472 5.49659C14.4377 5.73472 15.7221 5.02178 16.5014 5.47206C17.2923 5.92955 17.314 7.40305 17.9562 8.04383C18.5984 8.68461 20.0705 8.7077 20.528 9.49857C20.9782 10.2779 20.2653 11.5623 20.5034 12.4528C20.7329 13.3129 22 14.0663 22 15Z"
      } fill="#29ABE2"/>
      <path d={size === 'small'
        ? "M16.5876 7.60146C16.1229 7.13675 15.3696 7.13675 14.9049 7.60146L10.0312 12.4751L8.00786 10.4518C7.54315 9.98706 6.7898 9.98706 6.32509 10.4518C5.86039 10.9165 5.86039 11.6698 6.32509 12.1345L9.14798 14.9574C9.39188 15.2013 9.71372 15.3168 10.0327 15.3052C10.3516 15.3168 10.6734 15.2013 10.9173 14.9574L16.5891 9.28566C17.0538 8.82096 17.0538 8.06761 16.5891 7.60291L16.5876 7.60146Z"
        : "M16.5876 11.6015C16.1229 11.1368 15.3696 11.1368 14.9049 11.6015L10.0312 16.4751L8.00786 14.4518C7.54315 13.9871 6.7898 13.9871 6.32509 14.4518C5.86039 14.9165 5.86039 15.6698 6.32509 16.1345L9.14798 18.9574C9.39188 19.2013 9.71372 19.3168 10.0327 19.3052C10.3516 19.3168 10.6734 19.2013 10.9173 18.9574L16.5891 13.2857C17.0538 12.821 17.0538 12.0676 16.5891 11.6029L16.5876 11.6015Z"
      } fill="white"/>
      <text fill="#29ABE2" xmlSpace="preserve" style={{whiteSpace: 'pre'}} fontFamily="Poppins" fontSize={size === 'small' ? '16' : '17'} fontWeight="500" letterSpacing="0em">
        <tspan x="27" y={size === 'small' ? '16.6' : '20.95'}>Verified</tspan>
      </text>
    </svg>
  );

  const MembershipIcon = ({ planConfig }) => (
    <img 
      src={planConfig?.icon || 'https://api.builder.io/api/v1/image/assets/TEMP/da7f2e2f5640e47606909ff6c93615ea8c75e110?width=44'} 
      alt={planConfig?.label || 'Global'} 
      className="membership-icon"
    />
  );

  const CategoryIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.5 5H12V6H14.5V9H11V10H12.5V11.071C12.0287 11.1927 11.6179 11.4821 11.3447 11.885C11.0715 12.2879 10.9546 12.7766 11.0159 13.2595C11.0773 13.7424 11.3126 14.1864 11.6779 14.5082C12.0431 14.83 12.5132 15.0075 13 15.0075C13.4868 15.0075 13.9569 14.83 14.3221 14.5082C14.6874 14.1864 14.9227 13.7424 14.9841 13.2595C15.0454 12.7766 14.9285 12.2879 14.6553 11.885C14.3821 11.4821 13.9713 11.1927 13.5 11.071V10H14.5C14.7651 9.9996 15.0192 9.89412 15.2067 9.70667C15.3941 9.51922 15.4996 9.26509 15.5 9V6C15.5 5.73478 15.3946 5.48043 15.2071 5.29289C15.0196 5.10536 14.7652 5 14.5 5ZM14 13C14 13.1978 13.9414 13.3911 13.8315 13.5556C13.7216 13.72 13.5654 13.8482 13.3827 13.9239C13.2 13.9996 12.9989 14.0194 12.8049 13.9808C12.6109 13.9422 12.4327 13.847 12.2929 13.7071C12.153 13.5673 12.0578 13.3891 12.0192 13.1951C11.9806 13.0011 12.0004 12.8 12.0761 12.6173C12.1518 12.4346 12.28 12.2784 12.4444 12.1685C12.6089 12.0586 12.8022 12 13 12C13.2651 12.0004 13.5192 12.1059 13.7067 12.2933C13.8941 12.4808 13.9996 12.7349 14 13ZM9.5 3H7V4H9.5V7H6V8H7.5V11.071C7.02867 11.1927 6.61791 11.4821 6.3447 11.885C6.07149 12.2879 5.9546 12.7766 6.01594 13.2595C6.07728 13.7424 6.31263 14.1864 6.67788 14.5082C7.04314 14.83 7.51321 15.0075 8 15.0075C8.48679 15.0075 8.95686 14.83 9.32212 14.5082C9.68737 14.1864 9.92272 13.7424 9.98406 13.2595C10.0454 12.7766 9.92851 12.2879 9.6553 11.885C9.3821 11.4821 8.97133 11.1927 8.5 11.071V8H9.5C9.76522 8 10.0196 7.89464 10.2071 7.7071C10.3946 7.51957 10.5 7.26521 10.5 7V4C10.5 3.73478 10.3946 3.48043 10.2071 3.29289C10.0196 3.10536 9.76522 3 9.5 3ZM9 13C9 13.1978 8.94135 13.3911 8.83147 13.5556C8.72159 13.72 8.56541 13.8482 8.38268 13.9239C8.19996 13.9996 7.99889 14.0194 7.80491 13.9808C7.61093 13.9422 7.43275 13.847 7.29289 13.7071C7.15304 13.5673 7.0578 13.3891 7.01921 13.1951C6.98063 13.0011 7.00043 12.8 7.07612 12.6173C7.15181 12.4346 7.27998 12.2784 7.44443 12.1685C7.60888 12.0586 7.80222 12 8 12C8.26509 12.0004 8.51922 12.1059 8.70667 12.2933C8.89412 12.4808 8.9996 12.7349 9 13ZM4.5 1H1.5C1.23478 1 0.98043 1.10536 0.792893 1.29289C0.605357 1.48043 0.5 1.73478 0.5 2V5C0.5 5.26522 0.605357 5.51957 0.792893 5.70711C0.98043 5.89464 1.23478 6 1.5 6H2.5V11.071C2.02867 11.1927 1.61791 11.4821 1.3447 11.885C1.07149 12.2879 0.954603 12.7766 1.01594 13.2595C1.07728 13.7424 1.31263 14.1864 1.67788 14.5082C2.04314 14.83 2.51321 15.0075 3 15.0075C3.48679 15.0075 3.95686 14.83 4.32212 14.5082C4.68737 14.1864 4.92272 13.7424 4.98406 13.2595C5.0454 12.7766 4.92851 12.2879 4.6553 11.885C4.38209 11.4821 3.97133 11.1927 3.5 11.071V6H4.5C4.76522 6 5.01957 5.89464 5.20711 5.70711C5.39464 5.51957 5.5 5.26522 5.5 5V2C5.5 1.73478 5.39464 1.48043 5.20711 1.29289C5.01957 1.10536 4.76522 1 4.5 1ZM4 13C4 13.1978 3.94135 13.3911 3.83147 13.5556C3.72159 13.72 3.56541 13.8482 3.38268 13.9239C3.19996 13.9996 2.99889 14.0194 2.80491 13.9808C2.61093 13.9422 2.43275 13.847 2.29289 13.7071C2.15304 13.5673 2.0578 13.3891 2.01921 13.1951C1.98063 13.0011 2.00043 12.8 2.07612 12.6173C2.15181 12.4346 2.27998 12.2784 2.44443 12.1685C2.60888 12.0586 2.80222 12 3 12C3.26522 12 3.51957 12.1054 3.70711 12.2929C3.89464 12.4804 4 12.7348 4 13ZM1.5 5V2H4.5L4.501 5H1.5Z" fill="#6D6D6D"/>
    </svg>
  );

  const VideoPlayIcon = () => (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="16" fill="#0397DD" fillOpacity="0.2"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M16 28C17.5759 28 19.1363 27.6896 20.5922 27.0866C22.0481 26.4835 23.371 25.5996 24.4853 24.4853C25.5996 23.371 26.4835 22.0481 27.0866 20.5922C27.6896 19.1363 28 17.5759 28 16C28 14.4241 27.6896 12.8637 27.0866 11.4078C26.4835 9.95189 25.5996 8.62902 24.4853 7.51472C23.371 6.40042 22.0481 5.5165 20.5922 4.91345C19.1363 4.31039 17.5759 4 16 4C12.8174 4 9.76516 5.26428 7.51472 7.51472C5.26428 9.76515 4 12.8174 4 16C4 19.1826 5.26428 22.2348 7.51472 24.4853C9.76516 26.7357 12.8174 28 16 28ZM14.3773 10.6533L21.9027 14.8347C22.1104 14.9502 22.2835 15.1191 22.404 15.324C22.5245 15.5289 22.5881 15.7623 22.5881 16C22.5881 16.2377 22.5245 16.4711 22.404 16.676C22.2835 16.8809 22.1104 17.0498 21.9027 17.1653L14.3773 21.3467C14.1337 21.4821 13.8588 21.5515 13.5801 21.5481C13.3013 21.5446 13.0283 21.4683 12.7881 21.3269C12.5479 21.1854 12.3488 20.9836 12.2105 20.7416C12.0723 20.4995 11.9997 20.2254 12 19.9467V12.0533C11.9997 11.7746 12.0723 11.5005 12.2105 11.2585C12.3488 11.0164 12.5479 10.8146 12.7881 10.6731C13.0283 10.5317 13.3013 10.4554 13.5801 10.4519C13.8588 10.4485 14.1337 10.5179 14.3773 10.6533Z" fill="#1F396C"/>
    </svg>
  );

  const BookmarkIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22 0H11.5C11.1022 0 10.7206 0.158035 10.4393 0.43934C10.158 0.720644 10 1.10218 10 1.5V18C10.0001 18.1338 10.036 18.2652 10.1039 18.3805C10.1719 18.4958 10.2695 18.5908 10.3866 18.6557C10.5037 18.7206 10.636 18.7529 10.7698 18.7494C10.9036 18.7458 11.034 18.7066 11.1475 18.6356L16.75 15.1341L22.3534 18.6356C22.4669 18.7063 22.5972 18.7454 22.7309 18.7488C22.8646 18.7522 22.9967 18.7198 23.1136 18.655C23.2306 18.5902 23.3281 18.4953 23.396 18.3801C23.4639 18.2649 23.4998 18.1337 23.5 18V1.5C23.5 1.10218 23.342 0.720644 23.0607 0.43934C22.7794 0.158035 22.3978 0 22 0ZM22 16.6472L17.1466 13.6144C17.0274 13.5399 16.8896 13.5004 16.7491 13.5004C16.6085 13.5004 16.4708 13.5399 16.3516 13.6144L11.5 16.6472V1.5H22V16.6472Z" fill="#969696"/>
    </svg>
  );

  const ChevronLeftIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="28" height="28" rx="14" fill="#DBDBDB" fillOpacity="0.8"/>
      <path d="M17 20L11 14L17 8" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ChevronRightIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="28" height="28" rx="14" transform="matrix(-1 0 0 1 28 0)" fill="#DBDBDB" fillOpacity="0.8"/>
      <path d="M11 20L17 14L11 8" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const PDFIcon = () => (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M39.195 0H1.881C0.842142 0 0 0.842143 0 2.47629V70.7143C0 71.1579 0.842142 72 1.881 72H53.4047C54.4436 72 55.2857 71.1579 55.2857 70.7143V16.686C55.2857 15.7911 55.1661 15.5031 54.9553 15.291L39.9947 0.330429C39.7821 0.119152 39.4947 0.000393169 39.195 0Z" fill="#E9E9E0"/>
      <path d="M39.8594 0.193359V15.4278H55.0938L39.8594 0.193359Z" fill="#D9D7CA"/>
      <path d="M16.7323 42.8443C16.2849 42.8443 15.8554 42.699 15.489 42.4252C14.1506 41.421 13.9706 40.3037 14.0554 39.5426C14.2894 37.4494 16.8776 35.2586 21.7504 33.0266C23.6841 28.7889 25.524 23.5676 26.6207 19.2052C25.3376 16.4126 24.0904 12.7894 24.9994 10.6641C25.3183 9.91972 25.7156 9.34886 26.4574 9.102C26.8839 8.98267 27.3217 8.90855 27.7637 8.88086C28.4117 8.88086 28.9813 9.71529 29.385 10.2296C29.7643 10.713 30.6244 11.7377 28.9054 18.975C30.6386 22.5544 33.0943 26.2007 35.4471 28.6976C37.1327 28.3929 38.583 28.2373 39.7646 28.2373C41.778 28.2373 42.9981 28.7066 43.4957 29.6734C43.9071 30.4732 43.7387 31.4079 42.9943 32.4506C42.2781 33.4522 41.2907 33.9819 40.14 33.9819C38.5766 33.9819 36.756 32.9944 34.7258 31.044C31.0783 31.8064 26.8187 33.1667 23.3756 34.6723C22.3007 36.9532 21.2709 38.7905 20.3117 40.1379C18.9939 41.9829 17.8573 42.8443 16.7323 42.8443ZM20.1549 36.2537C17.4073 37.7979 16.2874 39.0669 16.2064 39.7817C16.1936 39.9 16.1589 40.2112 16.7606 40.6715C16.9521 40.611 18.0707 40.1006 20.1549 36.2537ZM37.6881 30.5426C38.736 31.3487 38.9918 31.7563 39.6771 31.7563C39.978 31.7563 40.8356 31.7434 41.2328 31.1893C41.4244 30.9206 41.499 30.7483 41.5286 30.6557C41.3704 30.5722 41.1608 30.4024 40.0178 30.4024C39.3686 30.4037 38.5521 30.432 37.6881 30.5426ZM28.0838 22.08C27.1213 25.3837 25.9735 28.6306 24.6459 31.8052C27.3676 30.7507 30.1581 29.883 32.9978 29.208C31.2621 27.192 29.5277 24.6746 28.0838 22.08ZM27.3034 11.2003C27.1774 11.2427 25.5934 13.4593 27.4268 15.3351C28.647 12.6159 27.3587 11.1823 27.3034 11.2003ZM53.4047 71.9992H1.881C1.38213 71.9992 0.903688 71.801 0.550932 71.4483C0.198176 71.0955 0 70.6171 0 70.1182V50.142H55.2857V70.1182C55.2857 71.157 54.4436 71.9992 53.4047 71.9992Z" fill="#CC4B4C"/>
      <path d="M13.9927 68.1424H11.8828V55.1875H15.6088C16.1591 55.1875 16.7042 55.2749 17.243 55.4511C17.7817 55.6272 18.2651 55.8908 18.6932 56.2418C19.1214 56.5928 19.4672 57.0184 19.7308 57.5159C19.9944 58.0135 20.1268 58.5728 20.1268 59.1951C20.1268 59.8521 20.015 60.4461 19.7925 60.9796C19.5852 61.4914 19.2668 61.9508 18.8604 62.3245C18.4618 62.6871 17.981 62.9686 17.4191 63.1679C16.8572 63.3672 16.235 63.4662 15.5561 63.4662H13.9914L13.9927 68.1424ZM13.9927 56.7869V61.9195H15.9264C16.1835 61.9195 16.4381 61.8758 16.6914 61.7871C16.9434 61.6996 17.1748 61.5556 17.3857 61.3564C17.5965 61.1571 17.7662 60.8794 17.8948 60.5219C18.0234 60.1645 18.0877 59.7222 18.0877 59.1951C18.0877 58.9842 18.0581 58.7399 18.0002 58.4661C17.9365 58.1803 17.8139 57.911 17.6402 57.6754C17.4386 57.4072 17.1765 57.1905 16.8752 57.0428C16.5474 56.8731 16.1128 56.7882 15.5741 56.7882L13.9927 56.7869ZM33.065 61.3049C33.065 62.3708 32.9505 63.2824 32.7217 64.0384C32.4928 64.7944 32.2035 65.4269 31.8513 65.9374C31.499 66.4478 31.1043 66.8489 30.6645 67.1421C30.2248 67.4352 29.8005 67.6538 29.3904 67.8017C29.0269 67.9367 28.6496 68.0311 28.2654 68.0832C27.926 68.1218 27.674 68.1424 27.5094 68.1424H22.6057V55.1875H26.5078C27.5981 55.1875 28.556 55.3611 29.3814 55.7056C30.2068 56.0502 30.8934 56.5118 31.4385 57.0852C31.9837 57.6586 32.39 58.3131 32.66 59.0446C32.93 59.7775 33.065 60.5309 33.065 61.3049ZM26.8074 66.5957C28.2371 66.5957 29.2683 66.1392 29.9008 65.2251C30.5334 64.3109 30.8497 62.9866 30.8497 61.2522C30.8497 60.7135 30.7854 60.1799 30.6568 59.6528C30.527 59.1256 30.2788 58.6486 29.9098 58.2205C29.5408 57.7924 29.0394 57.4465 28.4068 57.1829C27.7743 56.9194 26.954 56.7869 25.946 56.7869H24.7155V66.5957H26.8074ZM38.2683 56.7869V60.8652H43.6824V62.3065H38.2683V68.1424H36.1237V55.1875H44.2263V56.7869H38.2683Z" fill="white"/>
    </svg>
  );

  const ProductCard = ({ product }) => (
    <div className="sm-product-card">
      <div className="sm-product-image-container">
        <img src={product.image} alt={product.title} className="sm-product-image" />
      </div>
      <div className="sm-product-content">
        <div className="sm-product-info">
          <div className="sm-product-title-section">
            <p className="sm-product-title">{product.title}</p>
            <div className="sm-product-pricing">
              <div className="sm-product-price">
                <span className="sm-price-value">{product.price}</span>
                <span className="sm-price-unit"> / {product.unit}</span>
              </div>
              <div className="sm-product-moq">
                <span className="sm-moq-label">(MOQ) </span>
                <span className="sm-moq-value">{product.moq}</span>
              </div>
            </div>
          </div>
          <div className="sm-product-badges">
            <div className="sm-badge-row">
              {(() => {
                const planConfig = getPlanConfig(product.membershipType);
                return (
                  <div className="sm-membership-badge" style={{ backgroundColor: planConfig.color }}>
                    <MembershipIcon planConfig={planConfig} />
                    <span className="sm-membership-text">{planConfig.label}</span>
                  </div>
                );
              })()}
              {shouldShowVerifiedBadge(product.membershipType) && <VerifiedBadge size="large" />}
            </div>
            <img src={product.sellerLogo} alt="Seller" className="sm-seller-logo" />
            <div className="sm-location-info">
              <div className="sm-flag-container">
                <img src={product.countryFlag} alt={product.country} className="sm-country-flag" />
              </div>
              <span className="sm-location-text">
                <span className="sm-country">{product.country} / </span>
                <span className="sm-years">{product.yearsActive}</span>
              </span>
            </div>
            <div className="sm-business-type">
              <span className="sm-business-text">{product.businessType}</span>
            </div>
          </div>
        </div>
        <div className="sm-product-actions">
          <button className="sm-btn-inquire">Add To Quotation</button>
          <button className="sm-btn-call">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 16.92V19.92C22 20.4704 21.7893 20.9981 21.4142 21.3981C21.0391 21.7982 20.5304 22.0362 19.98 22.0699C19.643 22.0899 19.2989 22.0999 18.95 22.0999C10.21 22.0999 3.10001 14.9899 3.10001 6.24994C3.10001 5.90094 3.11001 5.55694 3.13001 5.21994C3.16376 4.66947 3.40177 4.16077 3.80183 3.78568C4.20189 3.41059 4.72954 3.19994 5.28001 3.19994H8.28001C8.72424 3.19557 9.15598 3.35553 9.49332 3.64969C9.83067 3.94386 10.0507 4.35171 10.11 4.79194C10.2199 5.66094 10.4293 6.51394 10.73 7.32994C10.8567 7.66661 10.8837 8.03161 10.808 8.38227C10.7323 8.73294 10.5571 9.05427 10.3 9.30994L9.00001 10.6099C10.3612 13.1755 12.4245 15.2387 14.99 16.5999L16.29 15.2999C16.5457 15.0429 16.8671 14.8677 17.2177 14.792C17.5684 14.7163 17.9334 14.7433 18.27 14.8699C19.086 15.1707 19.9391 15.3801 20.808 15.4899C21.2532 15.5498 21.6651 15.7746 21.9605 16.1183C22.256 16.462 22.4131 16.9009 22.4 17.3499L22 16.92Z" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Call</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="supplier-manage-modal-overlay" onClick={onClose}>
      <div className="supplier-manage-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header Section */}
        <div className="sm-header-section">
          <button className="sm-close-btn" onClick={onClose} aria-label="Close modal">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12" stroke="#DC3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4 4L12 12" stroke="#DC3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="sm-title-section">
            <h1 className="sm-modal-title">Supplier Manage</h1>
            <p className="sm-modal-subtitle">Effortlessly oversee and update your supplier profile, products, and verifications in one place.</p>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="sm-loading-container">
            <div className="sm-loading-spinner"></div>
            <p>Loading supplier details...</p>
          </div>
        ) : (
          <>
            {/* Cover Image */}
            <div className="sm-cover-image-section">
              <img src={data.coverImage} alt="Cover" className="sm-cover-image" />
            </div>

            {/* Company Info Section */}
            <div className="sm-company-section">
              <div className="sm-company-header">
            <div className="sm-company-info">
              <img src={data.logo} alt={data.name} className="sm-company-logo" />
              <div className="sm-company-details">
                <h2 className="sm-company-name">{data.name}</h2>
                <div className="sm-company-badges">
                  {(() => {
                    const planConfig = getPlanConfig(data.membershipType);
                    return (
                      <div className="sm-membership-badge" style={{ backgroundColor: planConfig.color }}>
                        <MembershipIcon planConfig={planConfig} />
                        <span className="sm-membership-text">{planConfig.label}</span>
                      </div>
                    );
                  })()}
                  {shouldShowVerifiedBadge(data.membershipType) && <VerifiedBadge size="small" />}
                  <div className="sm-location-badge">
                    <div className="sm-flag-container-header">
                      <img src={data.countryFlag} alt={data.country} className="sm-country-flag-header" />
                    </div>
                    <span className="sm-location-text-header">
                      <span className="sm-country-header">{data.country}</span>
                      <span className="sm-separator"> / </span>
                      <span className="sm-years-header">{data.yearsActive}</span>
                    </span>
                  </div>
                  <div className="sm-business-type-badge">
                    <CategoryIcon />
                    <span className="sm-business-type-text">{data.businessType}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="sm-stats-section">
              <span className="sm-stat">{data.reviewCount} Reviews</span>
              <div className="sm-stat-divider"></div>
              <span className="sm-stat">{data.productCount} Products</span>
            </div>
          </div>

          {/* Navigation Tabs - Hidden, show only profile content */}
          <div className="sm-nav-tabs" style={{ display: 'none' }}>
            <button 
              className={`sm-nav-tab ${activeTab === 'home' ? 'active' : ''}`}
              onClick={() => setActiveTab('home')}
            >
              Home
            </button>
            <button 
              className={`sm-nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Company Profile
            </button>
            <button 
              className={`sm-nav-tab ${activeTab === 'product' ? 'active' : ''}`}
              onClick={() => setActiveTab('product')}
            >
              Product
            </button>
            <button 
              className={`sm-nav-tab ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => setActiveTab('contact')}
            >
              Contact
            </button>
          </div>

          <button className="sm-bookmark-btn">
            <BookmarkIcon />
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'home' && (
          <>
            {/* Main Content */}
            <div className="sm-main-content">
              <div className="sm-content-row">
                <div className="sm-video-placeholder">
                  <VideoPlayIcon />
                </div>
                <div className="sm-content-details">
                  <div className="sm-company-overview">
                    <h3 className="sm-overview-title">{data.name}</h3>
                    <p className="sm-overview-subtitle">{data.tagline}</p>
                    <p className="sm-overview-description">{data.description}</p>
                  </div>
                  <div className="sm-contact-section">
                    <h4 className="sm-contact-title">Contact Us Now</h4>
                    <p className="sm-contact-description">{data.contactInfo.description}</p>
                    <div className="sm-action-buttons">
                      <button className="sm-btn-primary">Inquire Now</button>
                      <button className="sm-btn-secondary">View mobile</button>
                      <button className="sm-btn-secondary">Chat Now</button>
                    </div>
                  </div>
                  <div className="sm-certificates-section">
                    <h4 className="sm-certificates-title">Certificates & Trust Marks</h4>
                    <p className="sm-certificates-description">
                      {data.certificatesDescription} <span className="sm-see-more">See more</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Highlight Section */}
            <div className="sm-products-section">
              <div className="sm-section-header">
                <h3 className="sm-section-title">Product highlight</h3>
                <div className="sm-nav-arrows">
                  <button className="sm-arrow-btn">
                    <ChevronLeftIcon />
                  </button>
                  <button className="sm-arrow-btn">
                    <ChevronRightIcon />
                  </button>
                </div>
              </div>
              <div className="sm-products-grid">
                {data.products.slice(0, 5).map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>

            {/* Company Snapshot Section */}
            <div className="sm-snapshot-section">
              <div className="sm-section-header">
                <h3 className="sm-section-title">Company Snapshot</h3>
                <div className="sm-nav-arrows">
                  <button className="sm-arrow-btn">
                    <ChevronLeftIcon />
                  </button>
                  <button className="sm-arrow-btn">
                    <ChevronRightIcon />
                  </button>
                </div>
              </div>
              <div className="sm-snapshots-grid">
                {data.companySnapshots.map((snapshot, index) => (
                  <img key={index} src={snapshot} alt={`Snapshot ${index + 1}`} className="sm-snapshot-image" />
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'profile' && (
          <>
            {/* Main Content - Company Overview */}
            <div className="sm-main-content">
              <div className="sm-content-row">
                <div className="sm-video-placeholder">
                  <VideoPlayIcon />
                </div>
                <div className="sm-content-details">
                  <div className="sm-company-overview">
                    <h3 className="sm-overview-title">{data.name}</h3>
                    <p className="sm-overview-subtitle">{data.tagline}</p>
                    <p className="sm-overview-description">{data.description}</p>
                  </div>
                  <div className="sm-contact-section">
                    <h4 className="sm-contact-title">Contact Us Now</h4>
                    <p className="sm-contact-description">{data.contactInfo.description}</p>
                    <div className="sm-action-buttons">
                      <button className="sm-btn-primary" onClick={handleInquireNow}>Inquire Now</button>
                      <button className="sm-btn-secondary" onClick={handleViewMobile}>
                        {showMobile ? (data.contactInfo?.telephone || 'N/A') : 'View mobile'}
                      </button>
                      <button className="sm-btn-secondary" onClick={handleChatNow}>Chat Now</button>
                    </div>
                  </div>
                  <div className="sm-certificates-section">
                    <h4 className="sm-certificates-title">Certificates & Trust Marks</h4>
                    <p className="sm-certificates-description">
                      {data.certificatesDescription} <span className="sm-see-more" onClick={handleSeeMoreCertificates} style={{cursor: 'pointer'}}>{showCertificates ? 'Show less' : 'See more'}</span>
                    </p>
                    {showCertificates && (
                      <div className="sm-certificates-list">
                        <p>• ISO 9001:2015 Quality Management</p>
                        <p>• ISO 14001 Environmental Management</p>
                        <p>• CE Certified Products</p>
                        <p>• Trade Assurance Verified</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Basic Information Section */}
            <div className="sm-basic-info-section">
              <h3 className="sm-section-title">Basic Information</h3>
              <div className="sm-info-grid">
                <div className="sm-info-labels">
                  <span className="sm-info-label">Establish Year</span>
                  <span className="sm-info-label">License Number</span>
                  <span className="sm-info-label">Service Area</span>
                  <span className="sm-info-label">Number of Employee</span>
                  <span className="sm-info-label">Category</span>
                  <span className="sm-info-label">Annual Sale</span>
                  <span className="sm-info-label">Total Capitalization</span>
                  <span className="sm-info-label">Website Link</span>
                </div>
                <div className="sm-info-values">
                  <span className="sm-info-value">: {data.basicInfo?.establishYear || 'N/A'}</span>
                  <span className="sm-info-value">: {data.basicInfo?.licenseNumber || 'N/A'}</span>
                  <span className="sm-info-value">: {data.basicInfo?.serviceArea || 'N/A'}</span>
                  <span className="sm-info-value">: {data.basicInfo?.numberOfEmployees || 'N/A'}</span>
                  <span className="sm-info-value">: {data.basicInfo?.category || 'N/A'}</span>
                  <span className="sm-info-value">: {data.basicInfo?.annualSale || 'N/A'}</span>
                  <span className="sm-info-value">: {data.basicInfo?.totalCapitalization || 'N/A'}</span>
                  <span className="sm-info-value sm-link">: {data.basicInfo?.websiteLink || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="sm-contact-info-section">
              <h3 className="sm-section-title">Contact Information</h3>
              <div className="sm-info-grid">
                <div className="sm-info-labels">
                  <span className="sm-info-label">Telephone</span>
                  <span className="sm-info-label">Email</span>
                  <span className="sm-info-label">Address</span>
                  <span className="sm-info-label">Website</span>
                </div>
                <div className="sm-info-values">
                  <span className="sm-info-value">: {data.contactInfo?.telephone || 'N/A'}</span>
                  <span className="sm-info-value">: {data.contactInfo?.email || 'N/A'}</span>
                  <span className="sm-info-value">: {data.contactInfo?.address || 'N/A'}</span>
                  <span className="sm-info-value sm-link">: {data.contactInfo?.website || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Company Overview Section */}
            <div className="sm-overview-section">
              <h3 className="sm-section-title">Company Overview</h3>
              <p className="sm-overview-text">{data.companyOverview}</p>
            </div>

            {/* Company Notes Section */}
            <div className="sm-notes-section">
              <h3 className="sm-section-title">Company Notes</h3>
              <p className="sm-notes-text">{data.companyNotes}</p>
            </div>

            {/* Company Snapshot Section */}
            <div className="sm-snapshot-section">
              <div className="sm-section-header">
                <h3 className="sm-section-title">Company Snapshot</h3>
                <div className="sm-nav-arrows">
                  <button className="sm-arrow-btn">
                    <ChevronLeftIcon />
                  </button>
                  <button className="sm-arrow-btn">
                    <ChevronRightIcon />
                  </button>
                </div>
              </div>
              <div className="sm-snapshots-grid">
                {data.companySnapshots.map((snapshot, index) => (
                  <img key={index} src={snapshot} alt={`Snapshot ${index + 1}`} className="sm-snapshot-image" />
                ))}
              </div>
            </div>

            {/* Brochure Section */}
            <div className="sm-brochure-section">
              <h3 className="sm-section-title">Brochure</h3>
              <div className="sm-brochure-content">
                <PDFIcon />
                <button className="sm-download-btn">Download PDF</button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'product' && (
          <>
            {/* All Products Section */}
            <div className="sm-products-section">
              <div className="sm-section-header">
                <h3 className="sm-section-title">All Products ({data.products.length})</h3>
              </div>
              <div className="sm-products-grid">
                {data.products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              {data.products.length === 0 && (
                <div className="sm-no-products">
                  <p>No products available</p>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'contact' && (
          <>
            {/* Contact Form Section */}
            <div className="sm-contact-form-section">
              <div className="sm-contact-form-header">
                <h3 className="sm-section-title">Send Inquiry to {data.name}</h3>
                <p className="sm-contact-form-subtitle">Fill in the form below to send your inquiry directly to this supplier.</p>
              </div>
              
              {isOwnCompany ? (
                <div className="sm-own-company-warning">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="24" fill="#dc3545"/>
                    <path d="M24 14V26M24 34H24.02" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h4>This is Your Company</h4>
                  <p>You cannot send an inquiry to your own company.</p>
                </div>
              ) : (
              <div className="sm-contact-form">
                {formSuccess ? (
                  <div className="sm-form-success">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="24" cy="24" r="24" fill="#28A745"/>
                      <path d="M34 17L21 30L14 23" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <h4>Inquiry Sent Successfully!</h4>
                    <p>The supplier will contact you soon.</p>
                  </div>
                ) : (
                  <>
                    <div className="sm-form-row">
                      <div className="sm-form-group">
                        <label className="sm-form-label">Your Name *</label>
                        <input 
                          type="text" 
                          name="name"
                          className="sm-form-input" 
                          placeholder="Enter your full name"
                          value={contactForm.name}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div className="sm-form-group">
                        <label className="sm-form-label">Your Email *</label>
                        <input 
                          type="email" 
                          name="email"
                          className="sm-form-input" 
                          placeholder="Enter your email address"
                          value={contactForm.email}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>
                    <div className="sm-form-row">
                      <div className="sm-form-group">
                        <label className="sm-form-label">Phone Number</label>
                        <input 
                          type="tel" 
                          name="phone"
                          className="sm-form-input" 
                          placeholder="Enter your phone number"
                          value={contactForm.phone}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div className="sm-form-group">
                        <label className="sm-form-label">Company Name</label>
                        <input 
                          type="text" 
                          name="company"
                          className="sm-form-input" 
                          placeholder="Enter your company name"
                          value={contactForm.company}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>
                    <div className="sm-form-group full-width">
                      <label className="sm-form-label">Subject *</label>
                      <input 
                        type="text" 
                        name="subject"
                        className="sm-form-input" 
                        placeholder="Enter inquiry subject"
                        value={contactForm.subject}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="sm-form-group full-width">
                      <label className="sm-form-label">Message *</label>
                      <textarea 
                        name="message"
                        className="sm-form-textarea" 
                        placeholder="Describe your inquiry in detail..." 
                        rows="5"
                        value={contactForm.message}
                        onChange={handleFormChange}
                      ></textarea>
                    </div>
                    <div className="sm-form-actions">
                      <button 
                        className="sm-btn-primary sm-btn-submit"
                        onClick={handleFormSubmit}
                        disabled={formSubmitting}
                      >
                        {formSubmitting ? 'Sending...' : 'Send Inquiry'}
                      </button>
                      <button 
                        className="sm-btn-secondary"
                        onClick={handleFormCancel}
                        type="button"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
              )}

              {/* Contact Information Card */}
              <div className="sm-contact-info-card">
                <h4 className="sm-contact-card-title">Contact Information</h4>
                <div className="sm-contact-card-content">
                  {data.contactInfo?.telephone && (
                    <div className="sm-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18.3333 14.1V16.6C18.3343 16.8321 18.2866 17.0618 18.1936 17.2745C18.1006 17.4871 17.9643 17.6781 17.7933 17.8349C17.6222 17.9918 17.4203 18.1112 17.2005 18.1856C16.9806 18.26 16.7477 18.2876 16.5167 18.2667C13.9522 17.9881 11.489 17.1118 9.32499 15.7084C7.31151 14.4289 5.60443 12.7218 4.32499 10.7084C2.91663 8.53438 2.04019 6.05917 1.76665 3.48336C1.74583 3.25293 1.77321 3.02067 1.84707 2.80139C1.92092 2.58211 2.03963 2.38061 2.19562 2.20972C2.35162 2.03883 2.54149 1.90241 2.75314 1.80879C2.96479 1.71517 3.19374 1.66648 3.42499 1.66669H5.92499C6.32941 1.66273 6.72148 1.80594 7.02812 2.06965C7.33476 2.33336 7.53505 2.69958 7.59165 3.10003C7.69717 3.90006 7.89286 4.68563 8.17499 5.44169C8.2871 5.73999 8.31136 6.06414 8.24489 6.37577C8.17842 6.6874 8.02392 6.97346 7.79999 7.20003L6.74165 8.25836C7.92795 10.3447 9.65534 12.0721 11.7417 13.2584L12.8 12.2C13.0266 11.9761 13.3126 11.8216 13.6243 11.7551C13.9359 11.6887 14.26 11.7129 14.5583 11.825C15.3144 12.1071 16.0999 12.3028 16.9 12.4084C17.3047 12.4655 17.6744 12.6694 17.9386 12.9813C18.2029 13.2932 18.3433 13.6913 18.3333 14.1Z" stroke="#0397DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{data.contactInfo.telephone}</span>
                    </div>
                  )}
                  {data.contactInfo?.email && (
                    <div className="sm-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.33334 3.33331H16.6667C17.5833 3.33331 18.3333 4.08331 18.3333 4.99998V15C18.3333 15.9166 17.5833 16.6666 16.6667 16.6666H3.33334C2.41667 16.6666 1.66667 15.9166 1.66667 15V4.99998C1.66667 4.08331 2.41667 3.33331 3.33334 3.33331Z" stroke="#0397DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M18.3333 5L10 10.8333L1.66667 5" stroke="#0397DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{data.contactInfo.email}</span>
                    </div>
                  )}
                  {data.contactInfo?.address && (
                    <div className="sm-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.5 8.33331C17.5 14.1666 10 19.1666 10 19.1666C10 19.1666 2.50001 14.1666 2.50001 8.33331C2.50001 6.34419 3.29018 4.43653 4.6967 3.03001C6.10323 1.62349 8.01089 0.833313 10 0.833313C11.9891 0.833313 13.8968 1.62349 15.3033 3.03001C16.7098 4.43653 17.5 6.34419 17.5 8.33331Z" stroke="#0397DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 10.8333C11.3807 10.8333 12.5 9.71402 12.5 8.33331C12.5 6.9526 11.3807 5.83331 10 5.83331C8.61929 5.83331 7.5 6.9526 7.5 8.33331C7.5 9.71402 8.61929 10.8333 10 10.8333Z" stroke="#0397DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{data.contactInfo.address}</span>
                    </div>
                  )}
                  {data.contactInfo?.website && (
                    <div className="sm-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 9.99998C18.3333 5.39761 14.6024 1.66665 10 1.66665C5.39763 1.66665 1.66667 5.39761 1.66667 9.99998C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="#0397DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M1.66667 10H18.3333" stroke="#0397DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 1.66665C12.0844 3.94861 13.269 6.91003 13.3333 9.99998C13.269 13.0899 12.0844 16.0514 10 18.3333C7.91559 16.0514 6.73106 13.0899 6.66667 9.99998C6.73106 6.91003 7.91559 3.94861 10 1.66665Z" stroke="#0397DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{data.contactInfo.website}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Modal Footer with Actions */}
        <div className="sm-modal-footer">
          <button className="sm-footer-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="sm-footer-view-products-btn" onClick={onViewProducts}>
            View Products
          </button>
          <button className="sm-footer-approve-btn">Approve</button>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default SupplierManageModal;
